import type { Degree, Distance, Point } from "@pdfking/math"
import { Abs, Ceil, Cos, Sin, Tan, ToRadians, Max } from "@pdfking/math"

import type { PathPoint } from "#public"

/** The arc of `radius` round `center` from `start`, `sweep` degrees
 * counterclockwise (clockwise when negative), as cubic Béziers of at most 90°
 * each: a handle k = 4/3·tan(θ/4)·r along the tangent either side of every
 * anchor. The first point's `before` and the last's `after` sit on their
 * anchors.
 *
 * With `radius_y` it is an ellipse's: the circle's arc stretched along y, which
 * stretches its handles the same way, so the curves stay exact. */
export const ArcPoints = (
  center: Point<Distance>,
  radius: Distance,
  start: Degree,
  sweep: Degree,
  radius_y: Distance = radius
): PathPoint[] => {
  const pieces = Max(1, Ceil(Abs(sweep) / 90))
  const step = sweep / pieces
  const k = (4 / 3) * Tan(ToRadians((step / 4) as Degree))
  return Array.from({ length: pieces + 1 }, (_, i) => {
    const angle = ToRadians((start + step * i) as Degree)
    const [cos, sin] = [Cos(angle), Sin(angle)]
    const at = (along: number): Point<Distance> => ({
      x: (center.x + radius * (cos - along * sin)) as Distance,
      y: (center.y + radius_y * (sin + along * cos)) as Distance,
    })
    return { ...at(0), before: at(i > 0 ? -k : 0), after: at(i < pieces ? k : 0) }
  })
}
